import { FileText, FileDown } from "lucide-react";
import { useEffect, useState } from "react";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";
import { AnnualReportRow } from "@/utils/types/Screener/Screener";

interface NewsTabProps {
  company: any;
}

const NewsTab = ({ company }: NewsTabProps) => {
  const [annualReportData, setAnnualReportData] = useState<AnnualReportRow[]>(
    []
  );

  const fetchAnnualReportData = async () => {
    try {
      const response = await ScreenerService.getAnnualReportData(
        company.co_code
      );
      setAnnualReportData(response.data);
    } catch (error) {
      console.error("Error fetching annual report data:", error);
    }
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      // Fallback for cross-origin or other errors
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  useEffect(() => {
    fetchAnnualReportData();
  }, [company.id]);
  return (
    <div className="max-w-9xl mx-auto space-y-12 duration-500 animate-in fade-in">
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
        <div className="space-y-6">
          <h3 className="text-lg font-bold text-gray-900">Latest News</h3>
          <div className="space-y-6">
            {(
              company?.news ?? [
                {
                  title:
                    "Reliance Industries to acquire 100% stake in polyester maker.",
                  source: "The Economic Times",
                  time: "2h ago",
                  sentiment: "positive",
                },
                {
                  title: "RIL shares hit 52-week high after Q3 results.",
                  source: "Mint",
                  time: "5h ago",
                  sentiment: "positive",
                },
                {
                  title: "Jio Financial Services lists on exchanges.",
                  source: "CNBC",
                  time: "1d ago",
                  sentiment: "neutral",
                },
              ]
            ).map((news: any) => (
              <div
                key={`${news.source}-${news.time}-${news.title}`}
                className="group cursor-pointer"
              >
                <div className="mb-2 flex gap-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#00D09C]">
                    {news.source}
                  </span>
                  <span className="text-[10px] font-medium text-gray-400">
                    • {news.time}
                  </span>
                </div>
                <h4 className="mb-2 text-sm font-bold leading-snug text-gray-900 transition-colors group-hover:text-[#00D09C]">
                  {news.title}
                </h4>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-lg font-bold text-gray-900">Documents</h3>
          <div className="space-y-6">
            {Object.entries(
              (annualReportData ?? []).reduce(
                (acc, doc) => {
                  const year = doc.yrc ? Math.floor(doc.yrc / 100) : "Unknown";
                  if (!acc[year]) {
                    acc[year] = [];
                  }
                  acc[year].push(doc);
                  return acc;
                },
                {} as Record<string, AnnualReportRow[]>
              )
            )
              .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
              .map(([year, docs]) => (
                <div key={year} className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-500">
                    FY {year}
                  </h4>
                  {docs.map(doc => (
                    <div
                      key={doc.id}
                      onClick={() =>
                        doc.ar_url &&
                        handleDownload(doc.ar_url, `Annual_Report_${year}.pdf`)
                      }
                      className="flex cursor-pointer items-center justify-between rounded-xl border border-gray-200 p-4 transition-all hover:border-gray-200 hover:bg-gray-100/50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-gray-50 p-2">
                          <FileText className="h-5 w-5 text-gray-400" />
                        </div>
                        <div className="space-y-0.5">
                          <p className="text-sm font-semibold text-gray-900">
                            Annual Report {year}
                          </p>
                          {/* <p className="text-[10px] font-medium uppercase text-gray-400">
                            {new Date(doc.created_on).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </p> */}
                        </div>
                      </div>
                      <FileDown className="h-4 w-4 text-gray-300" />
                    </div>
                  ))}
                </div>
              ))}
            {(!annualReportData || annualReportData.length === 0) && (
              <p className="text-sm text-gray-500">No documents available.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsTab;
